import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// PDF de la cotización — estructura Odoo de LANG (bloque > REF / > CLIENTE,
// tabla con secciones, totales a la derecha, condiciones, pie lang.uy) con
// terminación fina: sin ítems vacíos, notas como sublínea gris, tabla de
// líneas horizontales, folio destacado arriba a la derecha y numeración.
// mode 'save' descarga; 'view' devuelve blob-URL; 'arraybuffer' para tests.

const INK = '#1f242e';
const SLATE = [63, 70, 82];     // header de tabla
const HAIR = [208, 212, 218];   // líneas finas
const BAND = [243, 244, 246];   // fila de sección
const CYAN = [101, 198, 224];   // línea del pie
const MUTED = '#6a7180';

function loadLogo(src) {
  return new Promise(resolve => {
    if (typeof Image === 'undefined') return resolve(null);
    const img = new Image();
    img.onload = () => {
      try {
        // Re-escalado: el PNG original pesa MB y jsPDF lo incrusta entero —
        // 240px de alto alcanza de sobra para 44pt impresos.
        const targetH = Math.min(240, img.naturalHeight);
        const scale = targetH / img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = targetH;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// "UYU 7.200" / "USD 2.388,50" — enteros sin decimales, decimales siempre en par
function money(n, currency) {
  const v = Number(n) || 0;
  const dec = Number.isInteger(Math.round(v * 100) / 100) ? 0 : 2;
  const num = new Intl.NumberFormat('es-UY', {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  }).format(v);
  return `${currency || 'UYU'} ${num}`;
}

function fmtDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function fmtQty(q) {
  const v = Number(q) || 0;
  return Number.isInteger(v) ? String(v) : String(v).replace('.', ',');
}

export function quotePdfFilename({ folio, title, clientName, days }) {
  const d0 = (days || []).find(d => d.date)?.date;
  const fecha = d0 ? `${d0.slice(8, 10)}.${d0.slice(5, 7)}.${d0.slice(2, 4)}` : null;
  const parts = [folio, title, clientName, fecha, 'LANG'].filter(Boolean);
  // Sin caracteres inválidos para nombre de archivo
  return parts.join(' - ').replace(/[/\\:*?"<>|]/g, '·') + '.pdf';
}

export async function generateQuotePdf({
  folio, title, clientName, lugarLabel, days, currency,
  items, totals, paymentTerms, notes,
}, { mode = 'save' } = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 52;
  const FOOT = 74; // reserva para el pie
  let y;

  // Línea fina superior, borde a borde
  doc.setDrawColor(...HAIR).setLineWidth(0.8);
  doc.line(0, 30, pageW, 30);

  // ── Cabecera: logo a la izquierda, folio destacado a la derecha ──
  const logo = await loadLogo('/logo2.png');
  if (logo) {
    const h = 40;
    const w = (logo.w / logo.h) * h;
    doc.addImage(logo.dataUrl, 'PNG', M, 50, w, h);
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(28).setTextColor(INK);
    doc.text('LANG', M, 80);
  }
  if (folio) {
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(MUTED);
    doc.text('P R E S U P U E S T O', pageW - M, 58, { align: 'right' });
    doc.setFont('helvetica', 'bold').setFontSize(19).setTextColor(INK);
    doc.text(folio, pageW - M, 78, { align: 'right' });
    const hoy = new Date();
    const emitido = `${String(hoy.getDate()).padStart(2, '0')}/${String(hoy.getMonth() + 1).padStart(2, '0')}/${hoy.getFullYear()}`;
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(MUTED);
    doc.text(`Emitido: ${emitido}`, pageW - M, 92, { align: 'right' });
  }
  y = 126;

  // ── Bloque de referencia (estilo Odoo) ──
  doc.setFontSize(10.5).setTextColor(INK);
  const metaLines = [];
  if (title) metaLines.push({ pre: '>', text: `REF: ${title.toUpperCase()}`, bold: true });
  if (clientName) metaLines.push({ pre: '>', text: `CLIENTE: ${clientName.toUpperCase()}`, bold: true });
  if (lugarLabel) metaLines.push({ pre: '-', text: `LUGAR: ${lugarLabel.toUpperCase()}`, bold: false });
  const realDays = (days || []).filter(d => d.date);
  realDays.forEach((d, i) => {
    const horario = [d.startTime, d.endTime].filter(Boolean).join(' a ');
    const lbl = realDays.length > 1 ? `DÍA ${i + 1}: ` : 'FECHA: ';
    metaLines.push({ pre: '-', text: `${lbl}${fmtDate(d.date)}${horario ? ` | HORARIO: ${horario}` : ''}`, bold: false });
  });
  for (const line of metaLines) {
    doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
    doc.text(`${line.pre} ${line.text}`, M, y);
    y += 19;
  }
  y += 12;

  doc.setFont('helvetica', 'bold').setFontSize(10.5).setTextColor(INK);
  doc.text('> COTIZACIÓN BASE', M, y);
  y += 12;

  // ── Tabla de ítems ──
  // Solo ítems reales: los renglones vacíos del editor no se imprimen.
  const isReal = i => (i.description || '').trim() || Number(i.unit_price) > 0;
  const sections = [...new Set(items.filter(i => i.is_header).map(i => i.section))];
  const anyDiscount = items.some(i => !i.is_header && isReal(i) && Number(i.discount) > 0);
  const printable = [];
  for (const section of sections) {
    const sectionItems = items.filter(i => i.section === section && !i.is_header && isReal(i));
    if (sectionItems.length) printable.push({ section, sectionItems });
  }
  const multiSection = printable.length > 1;

  const NCOLS = anyDiscount ? 5 : 4;
  const head = anyDiscount
    ? [['Descripción', 'Cant.', 'Precio Unitario', 'Dcto.', 'Total']]
    : [['Descripción', 'Cant.', 'Precio Unitario', 'Total']];

  const body = [];
  for (const { section, sectionItems } of printable) {
    if (multiSection) {
      body.push([{
        content: section.toUpperCase(),
        colSpan: NCOLS,
        styles: {
          fontStyle: 'bold', fontSize: 8.5, textColor: INK,
          fillColor: BAND, cellPadding: { top: 5, bottom: 5, left: 8 },
        },
        _band: true,
      }]);
    }
    for (const i of sectionItems) {
      const qty = Number(i.quantity) || 0;
      const price = Number(i.unit_price) || 0;
      const disc = Number(i.discount) || 0;
      const total = qty * price * (1 - disc / 100);
      const note = (i.note || '').trim();
      const descCell = {
        content: note ? `${i.description || ''}\n${note}` : (i.description || ''),
        _note: note || null,
        _title: i.description || '',
      };
      const row = [descCell, fmtQty(qty), money(price, currency)];
      if (anyDiscount) row.push(disc > 0 ? `${fmtQty(disc)}%` : '');
      row.push(money(total, currency));
      body.push(row);
    }
  }

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M, bottom: FOOT },
    head,
    body,
    theme: 'plain',
    styles: {
      font: 'helvetica', fontSize: 9.5, textColor: INK,
      cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
      valign: 'middle',
    },
    headStyles: {
      fillColor: SLATE, textColor: 255, fontStyle: 'bold', fontSize: 9,
      halign: 'center', cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    },
    columnStyles: anyDiscount ? {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 46, halign: 'center' },
      2: { cellWidth: 92, halign: 'right' },
      3: { cellWidth: 46, halign: 'center' },
      4: { cellWidth: 92, halign: 'right' },
    } : {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 52, halign: 'center' },
      2: { cellWidth: 100, halign: 'right' },
      3: { cellWidth: 100, halign: 'right' },
    },
    didParseCell: data => {
      if (data.section === 'head' && data.column.index === 0) data.cell.styles.halign = 'left';
      // La nota del ítem va en gris más chico — se pinta a mano en didDrawCell
      if (data.section === 'body' && data.column.index === 0 && data.cell.raw?._note) {
        data.cell.text = doc.splitTextToSize(data.cell.raw._title, 260);
        const noteLines = doc.splitTextToSize(data.cell.raw._note, 260).length;
        data.cell.styles.cellPadding = { top: 7, bottom: 7 + noteLines * 11, left: 8, right: 8 };
      }
    },
    didDrawCell: data => {
      if (data.section === 'body' && data.column.index === 0 && data.cell.raw?._note) {
        const lines = doc.splitTextToSize(data.cell.raw._note, data.cell.width - 16);
        doc.setFont('helvetica', 'normal').setFontSize(8.3).setTextColor(MUTED);
        let ny = data.cell.y + data.cell.height - 7 - (lines.length - 1) * 11;
        for (const ln of lines) { doc.text(ln, data.cell.x + 8, ny); ny += 11; }
        doc.setFontSize(9.5).setTextColor(INK);
      }
      // Línea horizontal fina bajo cada fila (sin verticales — más liviano)
      if (data.section === 'body' && data.column.index === data.table.columns.length - 1 && !data.row.raw[0]?._band) {
        doc.setDrawColor(...HAIR).setLineWidth(0.5);
        doc.line(M, data.cell.y + data.cell.height, pageW - M, data.cell.y + data.cell.height);
      }
    },
  });
  y = doc.lastAutoTable.finalY + 24;

  // ── Totales a la derecha ──
  const boxW = 214;
  const boxX = pageW - M - boxW;
  const rowsT = [{ lbl: 'Subtotal', val: money(totals.subtotal, currency), strong: false }];
  if (totals.discAmt > 0.01) rowsT.push({ lbl: 'Descuento', val: `- ${money(totals.discAmt, currency)}`, strong: false });
  rowsT.push({ lbl: 'IVA (22%)', val: money(totals.taxAmt, currency), strong: false });
  rowsT.push({ lbl: 'TOTAL', val: money(totals.total, currency), strong: true });

  const rowH = 22;
  const totalsH = rowsT.length * rowH + 8;
  if (y + totalsH > pageH - FOOT) { doc.addPage(); y = 60; }
  let ty = y;
  doc.setFontSize(9.5);
  for (const r of rowsT) {
    if (r.strong) {
      doc.setFillColor(...BAND);
      doc.rect(boxX, ty, boxW, rowH + 4, 'F');
      doc.setDrawColor(...SLATE).setLineWidth(1);
      doc.line(boxX, ty, boxX + boxW, ty);
      doc.setFont('helvetica', 'bold').setFontSize(10.5).setTextColor(INK);
      doc.text(r.lbl, boxX + 10, ty + 16);
      doc.text(r.val, boxX + boxW - 10, ty + 16, { align: 'right' });
      ty += rowH + 4;
    } else {
      doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(INK);
      doc.text(r.lbl, boxX + 10, ty + 14);
      doc.text(r.val, boxX + boxW - 10, ty + 14, { align: 'right' });
      doc.setDrawColor(...HAIR).setLineWidth(0.5);
      doc.line(boxX, ty + rowH, boxX + boxW, ty + rowH);
      ty += rowH;
    }
  }
  y = ty + 30;

  // ── Condiciones generales (con la forma de pago, como Odoo) ──
  const condLines = [];
  if (paymentTerms) condLines.push(`Forma de pago: ${paymentTerms}`);
  for (const raw of (notes || '').trim().split('\n')) {
    const line = raw.trim();
    if (line) condLines.push(line.replace(/^[-•]\s*/, ''));
  }
  if (condLines.length) {
    if (y + 40 + condLines.length * 15 > pageH - FOOT) { doc.addPage(); y = 60; }
    doc.setFont('helvetica', 'bold').setFontSize(10.5).setTextColor(INK);
    doc.text('> CONDICIONES GENERALES', M, y);
    y += 19;
    doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(INK);
    for (const line of condLines) {
      const wrapped = doc.splitTextToSize(`- ${line}`, pageW - M * 2);
      if (y + wrapped.length * 13 > pageH - FOOT) { doc.addPage(); y = 60; }
      doc.text(wrapped, M, y);
      y += wrapped.length * 13 + 4;
    }
  }

  // ── Pie en todas las páginas ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...CYAN).setLineWidth(1.4);
    doc.line(M, pageH - 46, pageW - M, pageH - 46);
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(MUTED);
    doc.text('lang.uy', M, pageH - 31);
    doc.text('@somoslang', pageW - M, pageH - 31, { align: 'right' });
    if (pages > 1) {
      doc.text(`Página ${p} de ${pages}`, pageW / 2, pageH - 31, { align: 'center' });
    }
  }

  if (mode === 'view') return doc.output('bloburl');
  if (mode === 'arraybuffer') return doc.output('arraybuffer');
  doc.save(quotePdfFilename({ folio, title, clientName, days }));
  return null;
}
